import type {MfaMethod} from "../../api/models";

// Signing in, two-factor authentication, and the pages opened from account emails.
export const auth = {
    signIn: {
        title: 'Sign in',
        expired: 'That took too long. Please sign in again.',
        // The API doesn't say which: it would tell someone whether an account exists.
        failed: 'Incorrect username, email address or password, or the account is disabled. An email address works only once it has been confirmed.',
        username: 'Username or email address',
        password: 'Password',
        submit: 'Sign in',
        forgotPassword: 'Forgot your password?',
        tooMany: 'Too many sign-in attempts. Wait 15 minutes and try again, or reset your password.',
        turnstileUnavailable: "The check that you're not a bot couldn't load, so you can't sign in. Check your connection, or try another browser.",
        // After the API ended the session at a renewal: someone else may have had it, or it moved country (a VPN, often).
        sessionEnded: 'For your security, we signed you out, because your session was used from somewhere unexpected. If it was you, just sign in again.'
    },

    // The password again, for the few changes that ask for it (components/PasswordConfirmation.tsx).
    confirmPassword: {
        title: 'Confirm your password',
        explanation: "For your security, enter your password to make this change. We won't ask again for a few minutes.",
        password: 'Password',
        incorrect: "That password isn't right.",
        submit: 'Confirm'
    },

    // Sign-in's second step.
    mfa: {
        title: 'Two-factor authentication',
        prompts: {
            totp: 'Enter the 6-digit code from your authenticator app.',
            email: 'Enter the 6-digit code we emailed you.',
            recovery: 'Enter one of your recovery codes. Each code works once.'
        } satisfies Record<MfaMethod, string>,
        // Links to switch to another of the account's methods.
        switchTo: {
            totp: 'Use your authenticator app',
            email: 'Email me a code',
            recovery: 'Use a recovery code'
        } satisfies Record<MfaMethod, string>,
        codeIncorrect: 'That code is incorrect or has expired.',
        tooManyAttempts: 'Too many attempts. Wait a few minutes and try again.',
        emailSent: 'A code is on its way. It expires in 10 minutes.',
        // Mail to the account's address bounces, so a code probably won't arrive.
        emailUndeliverable: "Email to your account's address isn't being delivered, so a code may not arrive. Use another way to sign in if you have one, or <link>open a support ticket</link>.",
        code: 'Code',
        recoveryCode: 'Recovery code',
        submit: 'Verify',
        sending: 'Sending…',
        sendCode: 'Send code',
        sendNewCode: 'Send a new code',
        signInAsSomeoneElse: 'Sign in as someone else'
    },

    forgotPassword: {
        title: 'Reset your password',
        // Said whether or not the address has an account.
        sent: "If an account uses that address, we've sent it a link to reset the password. The link works for 10 minutes.",
        intro: "Enter your account's email address and we'll send you a link.",
        email: 'Email address',
        submit: 'Send link',
        backToSignIn: 'Back to sign in',
        tooMany: 'Too many requests. Wait an hour and try again.',
        turnstileUnavailable: "The check that you're not a bot couldn't load. Check your connection, or try another browser."
    },

    // Opened from the "forgot password" email.
    resetPassword: {
        title: 'Choose a new password',
        noToken: 'This page needs the link from the email. <link>Request a new one</link>.',
        done: 'Your password has been changed. <link>Sign in</link>.',
        expired: 'This link has expired or was already used. Request a new one.',
        newPassword: 'New password',
        confirmPassword: 'Confirm new password',
        submit: 'Change password',
        requestNewLink: 'Request a new link'
    },

    // The same page, opened from the email a new account gets when a guest buys (?new=1).
    setPassword: {
        title: 'Choose your password',
        intro: 'Choose a password for your new account. Then you can sign in with your email address.',
        done: 'Your password is set, and your email address is confirmed. <link>Sign in</link> to see your license and order.',
        expired: 'This link has expired or was already used. Use "Request a new link" to choose a password another way.',
        submit: 'Set password'
    },

    // Opened from the "confirm your new email address" email.
    verifyEmail: {
        title: 'Confirm email address',
        // <link> is back to the profile when signed in, or to sign in when not.
        doneSignedIn: 'Your email address is confirmed, and you can use it to sign in. <link>Back to your profile</link>.',
        doneSignedOut: 'Your email address is confirmed, and you can use it to sign in. <link>Sign in</link>.',
        expired: 'This link has expired or is invalid. Send a new one from your profile.',
        emailInUse: 'That email address is already confirmed on another account.',
        noToken: 'This page needs the link from the email.'
    }
};

export type AuthMessages = typeof auth;
